import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useAnalytics } from '../../utils/analytics-provider/analytics-provider';
import {
  ImagePoster,
  CircleBackground,
  PlayButtonSmall,
  CircleLoadingIcon,
  VideoTitle
} from './styles';

const VideoPoster = ({
  poster,
  onPlayButtonClick,
  isVideoLoading,
  isVideoLoaded,
  className,
  size,
  title
}) => {
  const [disable, setDisable] = useState(() => false);

  const onClick = (e) => {
    e.stopPropagation();
    if (disable) return;

    setDisable(true);
    onPlayButtonClick();
  };

  return (
    <ImagePoster
      src={poster}
      isVideoLoaded={isVideoLoaded}
      className={className}
      size={size}
      onClick={onClick}
    >
      <CircleBackground size={size} onClick={onClick}>
        <PlayButtonSmall size={size} />
      </CircleBackground>

      <VideoTitle>{title}</VideoTitle>
      {isVideoLoading && <CircleLoadingIcon size={size} />}
    </ImagePoster>
  );
};

export const Video = ({ children, size, title, youtubeId, ...rest }) => {
  const { trackEvent } = useAnalytics();
  const videoRef = useRef(null);
  const videoState = useState(() => ({
    isLoading: true,
    buffered: 0,
    currentTime: 0,
    volume: 1,
    status: 'paused',
    duration: 0,
    isMuted: false
  }));
  const [isVideoLoading, setIsVideoLoading] = useState(() => false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(() => false);
  if (!youtubeId || youtubeId === ' ') return null;

  const videoId = encodeURIComponent(youtubeId);
  const posterUrl = `https://img.youtube.com/vi_webp/${videoId}/hqdefault.webp`;

  const onLoaded = (event) => {
    event.target.playVideo();
    SendEventData({ eventName: 'loaded', src: event.target.playerInfo.videoUrl });
  };

  const play = () => {
    new YT.Player(`iframe-${videoId}`, {
      videoId,
      ...(size === 'small' ? { width: 316, height: 187 } : {}),
      playerVars: {
        rel: '0',
        playsinline: 1,
        embed: 1,
        autoplay: 1,
        controls: 1
      },
      events: {
        onReady: onLoaded,
        onStateChange: (event) => {
          if (event.data === YT.PlayerState.ENDED) {
            SendEventData({ eventName: 'ended', src: event.target.playerInfo.videoUrl });
          } else if (event.data === YT.PlayerState.PAUSED) {
            SendEventData({ eventName: 'pause', src: event.target.playerInfo.videoUrl });
          } else if (event.data === YT.PlayerState.PLAYING) {
            SendEventData({ eventName: 'play', src: event.target.playerInfo.videoUrl });
          } else if (event.data === YT.PlayerState.BUFFERING) {
            SendEventData({ eventName: 'buffering', src: event.target.playerInfo.videoUrl });
          } else if (event.data === YT.PlayerState.CUED) {
            SendEventData({ eventName: 'cued', src: event.target.playerInfo.videoUrl });
          } else if (event.data === YT.PlayerState.UNSTARTED) {
            SendEventData({ eventName: 'unstarted', src: event.target.playerInfo.videoUrl });
          }
        }
      }
    });
  };

  const SendEventData = ({ eventName, src }) => {
    trackEvent('content_engagement_v2', { engagement_type: `video_${eventName}`, src });
  };

  const VideoPlayer = (
    <>
      <div ref={videoRef} id={`iframe-${videoId}`} data-src={videoId}></div>

      <div style={{ position: 'relative', display: 'flex' }}>
        <VideoPoster
          style={{ position: 'absolute', top: 0, left: 0 }}
          className={rest.className}
          poster={posterUrl}
          isVideoLoaded={isVideoLoaded}
          isVideoLoading={isVideoLoading}
          size={size}
          title={title}
          onPlayButtonClick={() => {
            setIsVideoLoading(true);
            setIsVideoLoaded(true);
            play();
          }}
        />
      </div>
    </>
  );

  return typeof children === 'function'
    ? children(VideoPlayer, videoState, { play, onLoaded }, videoRef)
    : VideoPlayer;
};

Video.propTypes = {
  youtubeId: PropTypes.string.isRequired,
  children: PropTypes.func,
  controls: PropTypes.bool,
  autoPlay: PropTypes.bool,
  preload: PropTypes.oneOf(['none', 'preload', 'metadata']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  onCanPlay: PropTypes.func,
  onError: PropTypes.func,
  onLoaded: PropTypes.func
};

Video.defaultProps = {
  defaultTime: 0,
  autoPlay: false,
  controls: true,
  preload: 'none',
  size: 'small'
};
